import React from 'react'
import { datalayerHandler } from '../../../../utils/datalayer'
import { useInView } from '../../../../utils/hooks/hooks/useInView'
import { Link } from '../../../../utils/links'
import { maybeToMailPlusLink } from '../../../../utils/mailplus'
import { expandBlockClasses } from '../../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode } from '../../../../utils/utils'
import { ExpandBlock, NestedBlock } from './expandBlockWidget'
import { LinkBlockNestedLayout } from './nestedBlocks/linkBlockNestedLayout'
import { MediaBlockNestedLayout } from './nestedBlocks/mediaBlockNestedLayout'
import { QuoteBlockNestedLayout } from './nestedBlocks/quoteBlockNestedLayout'
import { TextBlockNestedLayout } from './nestedBlocks/textBlockNestedLayout'

export const ExpandBlockLayout = (
  props: ExpandBlock & { expand: (_: number) => void } & {
    videoCookieAccepted: boolean
    acceptVideoCookie: () => void
  }
) => {
  const { inview, index, intro, primaryAction, title, items, expandBlockState, expand, videoCookieAccepted, acceptVideoCookie, } = props
  const classes = expandBlockClasses
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("AdvertisementBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const inviewClassName = getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')

  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div className={getClassName(expandBlockClasses, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(expandBlockClasses, 'headerClassName', '')}>
            {title && (
              <h2 className={getClassName(expandBlockClasses, 'titleClassName', 'mb-3')} id={`${index}`}>
                {title}
              </h2>
            )}
            {intro && <p className={getClassName(expandBlockClasses, 'introClassName', '')}>{intro}</p>}
          </header>
        )}

        {items.length > 0 && (
          <div className={getClassName(expandBlockClasses, 'contentClassName', '')}>
            <ul className={getClassName(expandBlockClasses, 'listClassName', 'accordion list-unstyled mb-3')}>
              {items.map((item, i) => {
                const expanded = expandBlockState.get(i)
                const { title, subtitle, SVGIcon, text, nestedBlocks } = item
                return (
                  <li
                    key={`expand-${i}-${index}`}
                    className={getClassName(expandBlockClasses, 'expandItemClassName', 'card')}
                  >
                    <button
                      className={getClassName(
                        expandBlockClasses,
                        'expandButtonClassName',
                        `card-header ${expanded ? 'col--open' : 'col--closed'} text-left border-0`
                      )}
                      aria-controls={`a11y-${i}-${index}`}
                      aria-expanded={expanded}
                      aria-label="Toggle item"
                      onClick={() => {
                        expanded ||
                          datalayerHandler('expand', title, 'expandable')
                        expand(i)
                      }}
                    >
                      {title && (
                        <p className={getClassName(expandBlockClasses, 'expandTitleClassName', 'btn btn-link m-0 p-0')}>
                          {title}
                        </p>
                      )}
                      {subtitle && (
                        <p className={getClassName(expandBlockClasses, 'expandSubtitleClassName', 'text-muted m-0')}>
                          {subtitle}
                        </p>
                      )}
                      {SVGIcon && (
                        <svg
                          className={getClassName(expandBlockClasses, 'expandIconClassName', 'mr-3')}
                          aria-hidden="true"
                          width={SVGIcon.width ? SVGIcon.width : '24px'}
                          height={SVGIcon.height ? SVGIcon.height : '24px'}
                        >
                          <use
                            xlinkHref={`${expanded
                                ? getClassName(expandBlockClasses, 'iconOpenId', '#icon--chevron-up')
                                : getClassName(expandBlockClasses, 'iconClosedId', '#icon--chevron-down')
                              }`}
                          />
                        </svg>
                      )}
                    </button>

                    <div
                      id={`a11y-${i}-${index}`}
                      className={getClassName(
                        expandBlockClasses,
                        'expandContentClassName',
                        `card-body pb-1 ${expanded ? '' : 'd-none'} text-left border-0`
                      )}
                      aria-hidden={!expanded}
                    >
                      {text && (
                        <div
                          className={getClassName(expandBlockClasses, 'expandTextClassName', '')}
                          dangerouslySetInnerHTML={{ __html: text }}
                        />
                      )}

                      {nestedBlocks.size > 0 &&
                        nestedBlocks.map((block, j) => (
                          <NestedblockSwitch
                            {...block}
                            key={`nested-${j}-${i}-${index}`}
                            videoCookieAccepted={videoCookieAccepted}
                            acceptVideoCookie={acceptVideoCookie}
                          />
                        ))}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {primaryAction && primaryAction.title && (
          <footer className={getClassName(expandBlockClasses, 'footerClassName', '')}>
            <div className={getClassName(expandBlockClasses, 'clickableWrapperClassName', 'd-inline-block')}>
              <Link
                url={primaryAction.url}
                title={primaryAction.title}
                className={getClassName(expandBlockClasses, 'clickableClassName', 'btn btn-dark d-inline-flex')}
                onClick={() =>
                  datalayerHandler('expand', undefined, undefined, {
                    kind: 'link',
                    title: convertReactNodeToString(title),
                    link: maybeToMailPlusLink(primaryAction.url),
                    label: primaryAction.title,
                  })
                }
              >
                <span className={getClassName(expandBlockClasses, 'clickableLabelClassName', '')}>
                  {htmlDecode(primaryAction.title)}
                </span>
                {primaryAction.SVGIcon && (
                  <svg
                    className={getClassName(expandBlockClasses, 'clickableIconClassName', 'ml-3')}
                    aria-hidden="true"
                    width={primaryAction.SVGIcon.width ? primaryAction.SVGIcon.width : '24px'}
                    height={primaryAction.SVGIcon.height ? primaryAction.SVGIcon.height : '24px'}
                  >
                    <use xlinkHref={getClassName(expandBlockClasses, 'iconId', '#icon--chevron-right-large')} />
                  </svg>
                )}
              </Link>
            </div>
          </footer>
        )}
      </div>
    </section>
  )
}

const NestedblockSwitch = (
  props: NestedBlock & {
    videoCookieAccepted: boolean
    acceptVideoCookie: () => void
    key: string
  }
): JSX.Element => {
  switch (props.kind) {
    case 'textBlockNested':
      return <TextBlockNestedLayout {...props} />
    case 'linkBlockNested':
      return <LinkBlockNestedLayout {...props} />
    case 'mediaBlockNested':
      return <MediaBlockNestedLayout {...props} />
    case 'quoteBlockNested':
      return <QuoteBlockNestedLayout {...props} />
  }
}
